import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Shield, Loader2, AlertCircle, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import ResultCard from '../components/ResultCard';
import ThemeToggle from '../components/ThemeToggle';
import apiService from '../services/api';
import BrandLogo from '../components/BrandLogo';

const SharedResultPage = () => {
  const { factCheckId } = useParams();
  const navigate = useNavigate();
  const [result, setResult] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchResult = async () => {
      try {
        setIsLoading(true);
        const data = await apiService.getFactCheck(factCheckId);
        setResult(data);
      } catch (err) {
        console.error('Error fetching shared result:', err);
        setError(err.response?.data?.detail || 'This verification could not be found or may have been deleted.');
      } finally {
        setIsLoading(false);
      }
    };

    if (factCheckId) {
      fetchResult();
    }
  }, [factCheckId]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
      {/* Header */}
      <header className="border-b border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md sticky top-0 z-40">
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <BrandLogo size="responsive" data-testid="brand-logo-header" />
            <span className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-blue-600 to-blue-800 dark:from-blue-400 dark:to-blue-600 bg-clip-text text-transparent">
              KaiCheckIt
            </span>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <ThemeToggle />
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              size="sm"
              className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm dark:border-slate-600 dark:text-slate-200"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Check a Claim</span>
              <span className="sm:hidden">Check</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 sm:px-6 py-6 sm:py-12">
        <div className="max-w-3xl mx-auto">
          {/* Shared Result Badge */}
          <div className="text-center mb-6">
            <span className="inline-flex items-center gap-2 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 px-4 py-2 rounded-full text-sm">
              <Shield className="w-4 h-4" />
              Shared Verification Result
            </span>
          </div>

          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <Loader2 className="w-12 h-12 sm:w-16 sm:h-16 text-blue-600 animate-spin mb-4" />
              <p className="text-slate-600 dark:text-slate-300">Loading verification...</p>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <Alert className="max-w-md mx-auto mb-6 bg-red-50 dark:bg-red-900/40 border-red-200 dark:border-red-700">
                <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
                <AlertDescription className="text-red-700 dark:text-red-300">{error}</AlertDescription>
              </Alert>
              <Button onClick={() => navigate('/')} className="bg-blue-600 hover:bg-blue-700">
                Check Your Own Claim
              </Button>
            </div>
          ) : result ? (
            <>
              <ResultCard result={result} />
              
              {/* CTA for new users */}
              <div className="mt-8 text-center bg-white dark:bg-slate-800 rounded-2xl p-6 shadow-lg border border-slate-200 dark:border-slate-700">
                <h3 className="text-lg font-semibold text-slate-800 dark:text-white mb-2">
                  Want to verify something?
                </h3>
                <p className="text-slate-600 dark:text-slate-400 mb-4 text-sm">
                  KaiCheckIt uses AI to verify claims in seconds. Try it free!
                </p>
                <Button 
                  onClick={() => navigate('/')} 
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  KaiCheckIt Now - It's Free
                </Button>
              </div>
            </>
          ) : null}
        </div>
      </main>
    </div>
  );
};

export default SharedResultPage;
